import React from 'react';

interface AudioPlayerProps {
  src?: string | null;
  audioRef?: React.MutableRefObject<HTMLAudioElement | null>;
  audioPlaying?: boolean;
  audioCurrentTime?: number;
  audioDuration?: number;
  onToggleAudioPlay?: () => void;
  onAudioSeek?: (time: number) => void;
  onSkipAudio?: (secs: number) => void;
  formatTime?: (s?: number) => string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  audioRef,
  audioPlaying = false,
  audioCurrentTime = 0,
  audioDuration = 0,
  onToggleAudioPlay,
  onAudioSeek,
  onSkipAudio,
  formatTime,
}) => {
  if (!src) return null;

  return (
    <div className="mb-2">
      <div className="w-full bg-black rounded-lg overflow-hidden border border-border-secondary p-3">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => onSkipAudio && onSkipAudio(-10)}
            aria-label="Rewind 10 seconds"
            className="w-9 h-9 flex items-center justify-center rounded-md bg-bg-secondary border border-border-secondary hover:bg-bg-tertiary"
          >
            <svg className="w-4 h-4 text-text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" d="M11 19V5l-7 7 7 7zM20 19V5l-7 7 7 7z" />
            </svg>
          </button>

          <button
            onClick={() => onToggleAudioPlay && onToggleAudioPlay()}
            aria-label={audioPlaying ? 'Pause' : 'Play'}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-yellow hover:brightness-95 border border-border-secondary"
          >
            {!audioPlaying ? (
              <svg className="w-5 h-5 text-text-black" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 3v18l15-9L5 3z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-text-black" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            )}
          </button>

          <button
            onClick={() => onSkipAudio && onSkipAudio(10)}
            aria-label="Forward 10 seconds"
            className="w-9 h-9 flex items-center justify-center rounded-md bg-bg-secondary border border-border-secondary hover:bg-bg-tertiary"
          >
            <svg className="w-4 h-4 text-text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" d="M4 19V5l7 7-7 7zM13 19V5l7 7-7 7z" />
            </svg>
          </button>

          <div className="flex-1 ml-3">
            <div
              className="relative h-2 bg-bg-secondary rounded-full cursor-pointer"
              onClick={(e) => {
                if (!onAudioSeek) return;
                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                const clickX = (e as React.MouseEvent).clientX - rect.left;
                const pct = Math.max(0, Math.min(1, clickX / rect.width));
                onAudioSeek((audioDuration || 0) * pct);
              }}
            >
              <div className="absolute left-0 top-0 bottom-0 bg-brand-yellow rounded-full" style={{ width: `${((audioDuration && audioDuration > 0) ? ((audioCurrentTime / audioDuration) * 100) : 0)}%` }} />
              <div
                className="absolute top-1/2 -translate-y-1/2 bg-white rounded-full"
                style={{ left: `calc(${((audioDuration && audioDuration > 0) ? ((audioCurrentTime / audioDuration) * 100) : 0)}% - 8px)`, width: 16, height: 16 }}
              />
            </div>
            <div className="flex justify-between text-xs text-text-tertiary mt-2">
              <div>{formatTime ? formatTime(audioCurrentTime) : '0:00'}</div>
              <div>{formatTime ? formatTime(audioDuration) : '0:00'}</div>
            </div>
          </div>
        </div>

        <audio
          id="analysis-audio-player"
          ref={audioRef}
          src={src}
          preload="metadata"
          className="w-full h-0 opacity-0 pointer-events-none"
        />
      </div>
    </div>
  );
};

export default AudioPlayer;
